import {useConfig} from '@aboutyou/config';
import {PromotionTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  Button,
  GenericIconName,
  Icon,
  IconSize,
  Text,
} from '@aboutyou/ui-components';
import {useBrandToggleFollow} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {useRef} from 'react';
import {useSubmitMissionProgress} from '../../backend-services/Coins/utils/useSubmitMissionProgress';
import {useAuthenticationPrompt} from '../Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {useIsAuthenticated} from '../Authentication/hooks/useIsAuthenticated';
import {useIsMissionsActive} from '../Coins/utils/useIsMissionsActive';

interface FollowBrandProps {
  className?: string;
  brand: {
    id: number;
    name: string;
    path: string;
  };
  hideFollowBrandLabel?: boolean;
  iconPosition?: 'left' | 'right';
  iconSize?: keyof typeof IconSize;
  onClick?: VoidFunction;
  promotionTracker?: PromotionTracker;
  scrollToTopAfterLogin?: boolean;
  style?: React.CSSProperties;
}

export const FollowBrand: React.FC<FollowBrandProps> = ({
  brand,
  className,
  hideFollowBrandLabel,
  iconPosition,
  iconSize,
  onClick,
  promotionTracker,
  style,
}) => {
  const {t} = useTranslation();
  const {device} = useConfig();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const isMissionActive = useIsMissionsActive();
  const submitLikeBrandMission = useSubmitMissionProgress({
    mission: 'likeBrandMission',
  });

  const {isFollowed, toggleFollow} = useBrandToggleFollow({
    brand,
    promotionTracker,
    onFollowSuccess: () => {
      if (isMissionActive) {
        submitLikeBrandMission(brand.id);
      }
    },
  });

  const {isAuthenticated} = useIsAuthenticated();
  const isAuthenticatedRef = useRef(isAuthenticated);

  // Update ref whenever isAuthenticated changes
  isAuthenticatedRef.current = isAuthenticated;

  const handleOnFollow = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClick?.();

    // Store the current authentication state before showing the prompt
    const wasAuthenticatedBeforePrompt = isAuthenticated;

    showUserAuthenticationPrompt({
      context: 'follow-brand',
      needsCheckoutSession: false,
      onSuccess: () => {
        // Use setTimeout to ensure the authentication state has updated
        setTimeout(() => {
          toggleFollow();

          const currentIsAuthenticated = isAuthenticatedRef.current;

          if (wasAuthenticatedBeforePrompt === undefined) {
            return;
          }

          if (wasAuthenticatedBeforePrompt === currentIsAuthenticated) {
            return;
          }

          // Scroll to top after login to prevent users from being stuck at the bottom of the page due to content changes on stream pages
          window.scrollTo(0, 0);
        });
      },
    });
  };

  const label = isFollowed ? t('Following') : t('Follow');

  return (
    <Wrapper
      data-testid="followBrand"
      className={className}
      style={style}
      onClick={handleOnFollow}
      aria-label={undefined}
      $iconPosition={iconPosition}
      size="fit-width"
    >
      <Fade mounted={!hideFollowBrandLabel} duration={250}>
        <Text color="White" size="px12" weight="w400">
          {label}
        </Text>
      </Fade>
      <Icon
        asset={
          isFollowed
            ? GenericIconName.CheckmarkCircle
            : GenericIconName.AddCircle
        }
        size={iconSize ?? (device === 'desktop' ? 'M' : 'S')}
        fill="White"
      />
    </Wrapper>
  );
};

const Wrapper = styled(Button)<{$iconPosition?: 'left' | 'right'}>`
  background: transparent;

  && {
    height: auto;
    padding: 16px;
  }

  & > span {
    display: flex;
    flex-direction: ${({$iconPosition}) =>
      $iconPosition === 'left' ? 'row-reverse' : 'row'};
    gap: 5px;
  }

  & > * > div:last-child {
    opacity: 0;
  }
`;
